import type { ToastData, ToastType, ToastOptions } from "./toast.types";

let counter = 0;

const generateId = (): string => {
  return `toast-${++counter}-${Date.now()}`;
};

export const TOAST_CREATE_EVENT = "e-toast:create";
export const TOAST_DISMISS_EVENT = "e-toast:dismiss";
export const TOAST_PAUSE_EVENT = "e-toast:pause";
export const TOAST_RESUME_EVENT = "e-toast:resume";

const VALID_TYPES = new Set<string>(["default", "success", "error", "warning", "info", "loading"]);
const MAX_TITLE_LENGTH = 200;
const MAX_DESCRIPTION_LENGTH = 500;

export const isValidToastData = (data: unknown): data is ToastData => {
  if (typeof data !== "object" || data === null) return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.id === "string" &&
    typeof d.title === "string" &&
    d.title.length <= MAX_TITLE_LENGTH &&
    (d.description === undefined ||
      (typeof d.description === "string" && d.description.length <= MAX_DESCRIPTION_LENGTH)) &&
    (d.type === undefined || VALID_TYPES.has(d.type as string))
  );
};

const DEDUP_WINDOW_MS = 50;
let lastKey = "";
let lastTime = 0;

const createToast = (title: string, type: ToastType, options?: ToastOptions): string => {
  const now = Date.now();
  const key = `${type}:${title}:${options?.description ?? ""}`;
  if (key === lastKey && now - lastTime < DEDUP_WINDOW_MS) return "";
  lastKey = key;
  lastTime = now;

  const id = generateId();
  const data: ToastData = { ...options, id, type, title };

  document.dispatchEvent(new CustomEvent(TOAST_CREATE_EVENT, { detail: data }));
  return id;
};

const toast = (title: string, options?: ToastOptions): string => {
  return createToast(title, "default", options);
};

toast.success = (title: string, options?: ToastOptions): string => createToast(title, "success", options);
toast.error = (title: string, options?: ToastOptions): string => createToast(title, "error", options);
toast.warning = (title: string, options?: ToastOptions): string => createToast(title, "warning", options);
toast.info = (title: string, options?: ToastOptions): string => createToast(title, "info", options);
toast.loading = (title: string, options?: ToastOptions): string => createToast(title, "loading", options);

toast.dismiss = (id?: string): void => {
  document.dispatchEvent(new CustomEvent(TOAST_DISMISS_EVENT, { detail: { id } }));
};

export { toast };
